<template>
  <ClientOnly>
    <div class="swiper">
      <div class="swiper-container">
        <div class="swiper-wrapper">
          <component :is="slide.component" v-for="(slide, index) in slides" :key="index" v-bind="slide.props" class="swiper-slide" />
        </div>
      </div>
      <div class="swiper-pagination">
        <div
          v-for="(item, index) in paginationCount"
          :key="`pagination${index}`"
          class="swiper-pagination__element"
          :class="{ 'swiper-pagination__element_active': index === activePagination }"
          @click="goToSlide(index)"
        />
      </div>
      <button
        v-if="renderNextPrevButtons"
        :class="['swiper-button', 'swiper-button-left']"
        :disabled="!hasPreviousPage"
        @click="goToPrevSlide"
      >
        <Icon name="CaretLeft" :color="hasPreviousPage ? 'white' : 'grey'" />
      </button>
      <button
        v-if="renderNextPrevButtons"
        :class="['swiper-button', 'swiper-button-right']"
        :disabled="!hasNextPage"
        @click="goToNextSlide"
      >
        <Icon name="CaretLeft" :color="hasNextPage ? 'white' : 'grey'" />
      </button>
    </div>
  </ClientOnly>
</template>

<script>
import { Swiper } from 'swiper'
import 'swiper/swiper-bundle.min.css'

import Icon from '@/components/common/Icon/Icon'

export default {
  components: {
    Icon
  },
  props: {
    slides: {
      type: Array,
      required: true
    },
    slidesPerGroup: {
      type: Number,
      default: 2
    },
    spaceBetween: {
      type: Number,
      default: 24
    },
    renderNextPrevButtons: {
      type: Boolean,
      default: true
    },
    smartBreakPointSlidesPerGroup: {
      type: Number,
      default () {
        return this.slidesPerGroup
      }
    },
    phabletBreakPointSlidesPerGroup: {
      type: Number,
      default () {
        return this.slidesPerGroup
      }
    }
  },

  data () {
    return {
      swiper: null
    }
  },

  computed: {
    paginationCount () {
      return Math.ceil(this.itemsCount / this.activeCountOfSlidesPerSlide)
    },

    itemsCount () {
      return this.slides.length
    },

    activePagination () {
      if (!this.swiper) {
        return -1
      }
      return Math.ceil(this.swiper.activeIndex / this.activeCountOfSlidesPerSlide)
    },

    hasNextPage () {
      return this.activePagination !== this.paginationCount - 1
    },

    hasPreviousPage () {
      return this.activePagination !== 0
    },

    activeCountOfSlidesPerSlide () {
      if (!this.swiper) {
        return 1
      }

      return this.swiper.params.slidesPerGroup
    }
  },

  mounted () {
    this.$nextTick(() => {
      this.swiper = new Swiper('.swiper-container', {
        slidesPerGroup: this.slidesPerGroup,
        slidesPerView: this.slidesPerGroup,
        spaceBetween: this.spaceBetween,
        direction: 'horizontal',
        loop: false,
        navigation: this.renderNextPrevButtons
          ? {
              nextEl: '.swiper-button-prev',
              prevEl: '.swiper-button-next'
            }
          : null,
        pagination: {
          el: '.swiper-pagination'
        },
        breakpoints: {
          480: {
            slidesPerGroup: this.smartBreakPointSlidesPerGroup,
            slidesPerView: this.smartBreakPointSlidesPerGroup
          },
          600: {
            slidesPerGroup: this.phabletBreakPointSlidesPerGroup,
            slidesPerView: this.phabletBreakPointSlidesPerGroup
          }
        }
      })
    })
  },

  methods: {
    goToNextSlide () {
      this.swiper.slideNext()
    },

    goToPrevSlide () {
      this.swiper.slidePrev()
    },

    goToSlide (index) {
      this.swiper.slideTo(index * this.activeCountOfSlidesPerSlide)
    }
  }
}
</script>

<style lang="scss" scoped>
.swiper {
  position: relative;

  &-pagination {
    position: relative;
    display: flex;
    justify-content: center;
    width: fit-content;
    margin-left: auto;
    margin-right: auto;
    padding: $gap * 0.75 0;
    margin-top: $gap * 2;
    gap: $gap * 0.5;

    &__element {
      width: 24px;
      height: 12px;
      cursor: pointer;
      border-radius: 4px;
      box-sizing: border-box;
      transition: all .2s ease;
      background: rgba(0, 0, 0, 0.24);
      border: 1px solid #594F68;

      &:not(.swiper-pagination__element_active) {
        &:hover {
          background: rgba(#594F68, 0.3);
        }
      }

      &_active {
        border-color: #fff;
        background-color: #fff;
      }
    }
  }

  &-slide {
    height: auto;
  }

  &-button {
    position: absolute;
    top: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 100%;
    border: 1px solid rgba(#F1EFFF4D, 0.30);
    background-color: rgba(#0000003D, 0.24);
    width: 40px;
    height: 40px;

    @include display(desktop) {
      &:enabled {
        cursor: pointer;
      }

      &:hover:enabled{
        background-color: rgba(#F1EFFF4D, 0.05);
      }
    }

    @include display-less(hd) {
      top: unset;
      bottom: $gap * -1.5;
    }

    &-left {
      transform: translate(-100%, -50%);
      left: $gap * -0.5;

      @include display-less(hd) {
        left: $gap * 2.5;
      }
    }

    &-right {
      transform: translate(-100%, 50%);
      rotate: 180deg;
      right: $gap * -0.5;

      @include display-less(hd) {
        right: $gap * 2.5;
      }
    }
  }
}
</style>
